import React, { useState } from 'react';
import { MediaGroup as MediaGroupType, ImageMedia, VideoMedia, FileMedia } from '../../types';
import ImageDisplay from './ImageDisplay';
import VideoDisplay from './VideoDisplay';
import FileDisplay from './FileDisplay';

interface MediaGroupProps {
  mediaGroup: MediaGroupType;
  className?: string;
}

const MediaGroup: React.FC<MediaGroupProps> = ({ mediaGroup, className = '' }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Function to determine layout classes based on layout type
  const getLayoutClasses = () => {
    switch (mediaGroup.layout) {
      case 'carousel':
        return 'relative';
      case 'grid':
        return 'grid gap-2 ' + (
          mediaGroup.items.length === 1 ? 'grid-cols-1' :
          mediaGroup.items.length === 2 ? 'grid-cols-2' :
          mediaGroup.items.length === 3 ? 'grid-cols-3' :
          mediaGroup.items.length === 4 ? 'grid-cols-2 grid-rows-2' :
          'grid-cols-3'
        );
      case 'list':
      default:
        return 'flex flex-col space-y-3';
    }
  };

  // Render a single media item based on its type
  const renderMediaItem = (item: ImageMedia | VideoMedia | FileMedia, index: number) => {
    if ('url' in item && 'name' in item && 'type' in item) {
      // It's a file
      return <FileDisplay key={index} file={item as FileMedia} />;
    } else if ('url' in item && 'provider' in item) {
      // It's a video
      return <VideoDisplay key={index} video={item as VideoMedia} />;
    } else if ('url' in item) {
      // It's an image
      return <ImageDisplay key={index} image={item as ImageMedia} />;
    }
    
    return null;
  };

  // Render media items based on layout
  const renderMediaItems = () => {
    if (mediaGroup.layout === 'carousel') {
      return (
        <div className="relative">
          {/* Current active item */}
          <div className="transition-opacity duration-300">
            {renderMediaItem(mediaGroup.items[activeIndex], activeIndex)}
          </div>
          
          {/* Navigation dots for carousel */}
          {mediaGroup.items.length > 1 && (
            <div className="flex justify-center mt-3 space-x-2">
              {mediaGroup.items.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === activeIndex ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
          
          {/* Previous/Next buttons */}
          {mediaGroup.items.length > 1 && (
            <>
              <button
                onClick={() => setActiveIndex(prev => (prev === 0 ? mediaGroup.items.length - 1 : prev - 1))}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-70 rounded-full p-1 shadow hover:bg-opacity-100 transition-colors"
                aria-label="Previous item"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => setActiveIndex(prev => (prev === mediaGroup.items.length - 1 ? 0 : prev + 1))}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-70 rounded-full p-1 shadow hover:bg-opacity-100 transition-colors"
                aria-label="Next item"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
      );
    }
    
    // Grid or list layout
    return mediaGroup.items.map((item, index) => renderMediaItem(item, index));
  };

  return (
    <div className={`media-group ${getLayoutClasses()} ${className}`}>
      {renderMediaItems()}
    </div>
  );
};

export default MediaGroup; 